import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axios';
import ActivityHeader from './ActivityHeader';
import ActivityTimeline from './ActivityTimeline';
import ActivityBreakdown from './ActivityBreakdown';
import ActivityAssociations from './ActivityAssociations';
import ActivityEvents from './ActivityEvents';

export default function ActivityProfile() {
  const { entityType, entityId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchProfile();
  }, [entityType, entityId, timeRange]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `/logs/activity/profile/${entityType}/${encodeURIComponent(entityId)}`,
        { params: { timeRange } }
      );

      setProfileData(response.data.data);
    } catch (err) {
      console.error('Error fetching activity profile:', err);
      setError(err.response?.data?.error?.message || 'Failed to load activity profile');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Profile</h2>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={fetchProfile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header with entity info and stats */}
        <ActivityHeader
          entity={{
            type: entityType,
            id: entityId,
            displayName: profileData?.entity?.displayName || entityId,
          }}
          summary={profileData?.summary}
          stats={profileData?.stats}
          geo={profileData?.geo}
          security={profileData?.security}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          loading={loading}
        />

        {/* Timeline chart - full width */}
        <ActivityTimeline
          entityType={entityType}
          entityId={entityId}
          timeRange={timeRange}
        />

        {/* Two column layout for breakdown and associations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityBreakdown
            entityType={entityType}
            entityId={entityId}
            timeRange={timeRange}
          />
          <ActivityAssociations
            entityType={entityType}
            entityId={entityId}
            timeRange={timeRange}
          />
        </div>

        {/* Events list - full width */}
        <ActivityEvents
          entityType={entityType}
          entityId={entityId}
          timeRange={timeRange}
        />
      </div>
    </div>
  );
}
